import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { User, Phone, FileText, Paperclip, CheckCircle, AlertCircle } from 'lucide-react';
import { Logo } from '../components/Logo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function GuestConsultation() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    full_name: '',
    phone: '',
    subject: '',
    message: '',
    attachments: ['', '', '', '', ''],
  });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'message') {
      if (value.length <= 1000) {
        setFormData({ ...formData, [name]: value });
        setCharCount(value.length);
      }
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleAttachmentChange = (index, value) => {
    const newAttachments = [...formData.attachments];
    newAttachments[index] = value;
    setFormData({ ...formData, attachments: newAttachments });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const filteredAttachments = formData.attachments.filter((att) => att.trim() !== '');
      const payload = {
        full_name: formData.full_name,
        phone: formData.phone,
        subject: formData.subject,
        message: formData.message,
        attachments: filteredAttachments,
      };

      await axios.post(`${API}/guest-consultations`, payload);
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'فشل إرسال الطلب. الرجاء المحاولة مرة أخرى');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center" dir="rtl" lang="ar">
        <div className="max-w-2xl w-full mx-4">
          <div className="bg-white rounded-xl shadow-xl p-8 text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-3xl font-heading font-bold text-primary mb-4">
              تم إرسال طلبك بنجاح!
            </h2>
            <p className="text-lg text-gray-600 mb-6 font-body">
              شكراً لتواصلك معنا. سيتم مراجعة طلبك من قبل أحد محامينا وسنتواصل معك قريباً على رقم
              الجوال المُدخل.
            </p>
            <div className="flex gap-4 justify-center">
              <button
                data-testid="back-to-home-btn"
                onClick={() => navigate('/')}
                className="px-8 py-3 bg-primary text-white rounded-md hover:bg-primary-900 transition-all duration-300 font-heading"
              >
                العودة للرئيسية
              </button>
              <button
                data-testid="new-consultation-btn"
                onClick={() => {
                  setSubmitted(false);
                  setFormData({
                    full_name: '',
                    phone: '',
                    subject: '',
                    message: '',
                    attachments: ['', '', '', '', ''],
                  });
                  setCharCount(0);
                }}
                className="px-8 py-3 border border-primary text-primary rounded-md hover:bg-primary hover:text-white transition-all duration-300 font-heading"
              >
                طلب استشارة جديدة
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sand py-12" dir="rtl" lang="ar">
      {/* Header */}
      <header className="bg-primary py-4 shadow-lg mb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Logo size="md" />
            <h1 className="text-2xl font-heading font-bold text-white">
              طلب استشارة قانونية
            </h1>
          </div>
          <button
            data-testid="back-to-home-btn"
            onClick={() => navigate('/')}
            className="px-6 py-2 text-white border border-white/30 rounded-md hover:bg-white hover:text-primary transition-all duration-300 font-body"
          >
            العودة للرئيسية
          </button>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4">
        <div className="bg-white rounded-xl shadow-xl p-8">
          <div className="mb-8 text-center">
            <h2 className="text-3xl font-heading font-bold text-primary mb-3">
              نحن هنا لمساعدتك
            </h2>
            <p className="text-base text-gray-600 font-body">
              يمكنك طلب استشارة قانونية دون الحاجة لإنشاء حساب. املأ النموذج أدناه وسنتواصل معك
              قريباً.
            </p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md flex items-center gap-3" data-testid="error-message">
              <AlertCircle className="w-5 h-5 text-red-500" />
              <p className="text-sm text-red-700 font-body">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* الاسم */}
            <div>
              <label className="block text-sm font-medium text-primary mb-2 text-right font-body">
                الاسم الكامل <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <User className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  data-testid="guest-name-input"
                  type="text"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleChange}
                  required
                  className="w-full pr-10 pl-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-gold focus:border-transparent outline-none transition-all font-body"
                  placeholder="أدخل اسمك الكامل"
                />
              </div>
            </div>

            {/* رقم الجوال */}
            <div>
              <label className="block text-sm font-medium text-primary mb-2 text-right font-body">
                رقم الجوال <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Phone className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  data-testid="guest-phone-input"
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                    setFormData({ ...formData, phone: val });
                  }}
                  required
                  maxLength={10}
                  className="w-full pr-10 pl-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-gold focus:border-transparent outline-none transition-all font-body"
                  placeholder="05xxxxxxxx"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1 font-body">
                {formData.phone.length}/10 أرقام
                {formData.phone.length === 10 && <span className="text-green-600 mr-2">✓</span>}
              </p>
            </div>

            {/* موضوع القضية */}
            <div>
              <label className="block text-sm font-medium text-primary mb-2 text-right font-body">
                موضوع القضية أو الاستشارة <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <FileText className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  data-testid="guest-subject-input"
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  className="w-full pr-10 pl-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-gold focus:border-transparent outline-none transition-all font-body"
                  placeholder="مثال: استشارة في قضية عمالية"
                />
              </div>
            </div>

            {/* تفاصيل القضية */}
            <div>
              <label className="block text-sm font-medium text-primary mb-2 text-right font-body">
                شرح تفاصيل القضية أو الاستشارة <span className="text-red-500">*</span>
              </label>
              <textarea
                data-testid="guest-message-input"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                rows={8}
                maxLength={1000}
                className="w-full px-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-gold focus:border-transparent outline-none transition-all font-body"
                placeholder="اكتب تفاصيل القضية أو الاستشارة بشكل واضح ومفصل..."
              />
              <div className="flex justify-between items-center mt-2">
                <p className="text-xs text-gray-500 font-body">الحد الأقصى 1000 حرف</p>
                <p
                  className={`text-xs font-medium font-body ${
                    charCount > 950 ? 'text-red-500' : 'text-gray-600'
                  }`}
                >
                  {charCount} / 1000
                </p>
              </div>
            </div>

            {/* المرفقات */}
            <div>
              <label className="block text-sm font-medium text-primary mb-2 text-right font-body">
                المرفقات (اختياري - حتى 5 مرفقات)
              </label>
              <p className="text-xs text-gray-500 mb-3 font-body">
                يمكنك إضافة روابط للمستندات أو الملفات المحفوظة على خدمات التخزين السحابي
              </p>
              <div className="space-y-3">
                {formData.attachments.map((attachment, index) => (
                  <div key={index} className="relative">
                    <Paperclip className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      data-testid={`guest-attachment-${index + 1}-input`}
                      type="url"
                      value={attachment}
                      onChange={(e) => handleAttachmentChange(index, e.target.value)}
                      className="w-full pr-10 pl-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-gold focus:border-transparent outline-none transition-all font-body"
                      placeholder={`رابط المرفق ${index + 1}`}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* زر الإرسال */}
            <button
              data-testid="guest-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full bg-gold text-white py-4 rounded-md hover:bg-gold-dark transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 font-heading font-bold text-lg"
            >
              {loading ? 'جاري الإرسال...' : 'إرسال الطلب'}
            </button>
          </form>

          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
            <p className="text-sm text-blue-800 font-body">
              <strong>ملاحظة:</strong> بإرسالك لهذا الطلب، فإنك توافق على سياسة الخصوصية الخاصة بنا
              ومعالجة بياناتك لأغراض تقديم الاستشارة القانونية.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
